/**
 * Centralized configuration for application settings, theming, and metadata defaults.
 */

import { Language } from '@cigarsnap/shared/types';

/**
 * @interface AppConfig
 * Defines the structure for the application's central configuration.
 * This includes application identity, supported languages, theme settings (colors, fonts, logos),
 * SEO defaults, external links, and optional feature flags.
 */
export interface AppConfig {
    /** @property {string} appNameKey - Translation key used to fetch the application's name. */
    appNameKey: string;
    /** @property {string} appDescriptionKey - Translation key for the default application description (used in meta tags, etc.). */
    appDescriptionKey: string;
    /** @property {Language[]} supportedLanguages - Array of language codes supported by the application. */
    supportedLanguages: Language[];
    /** @property {Language} defaultLanguage - The default language used if no locale is specified or detected. */
    defaultLanguage: Language;
    /** @property {number} trialMaxApiCalls - The maximum number of API calls allowed during a trial. */
    trialMaxApiCalls: number;
    /** @property {object} theme - Contains configuration related to the application's visual theme. */
    theme: {
        /** @property {object} colors - Defines the application's color palette, referencing CSS variables. */
        colors: {
            /** @property {object} primary - Primary color set (default and foreground). */
            primary: { DEFAULT: string; foreground: string; darker: string };
            /** @property {object} secondary - Secondary color set (default and foreground). */
            secondary: { DEFAULT: string; foreground: string };
            /** @property {object} destructive - Destructive color set (default and foreground). */
            destructive: { DEFAULT: string; foreground: string };
            /** @property {object} accent - Accent color set (default and foreground). */
            accent: { DEFAULT: string; foreground: string };
            /** @property {string} background - Background color. */
            background: string;
            /** @property {string} foreground - Default foreground text color. */
            foreground: string;
            /** @property {object} card - Card background and foreground colors. */
            card: { DEFAULT: string; foreground: string };
            /** @property {object} popover - Popover background and foreground colors. */
            popover: { DEFAULT: string; foreground: string };
            /** @property {object} muted - Muted background and foreground colors. */
            muted: { DEFAULT: string; foreground: string };
            /** @property {string} border - Default border color. */
            border: string;
            /** @property {string} input - Input field border color. */
            input: string;
            /** @property {string} ring - Focus ring color. */
            ring: string;
        };
        /** @property {object} fontFamily - Defines font families, referencing CSS variables. */
        fontFamily: {
            /** @property {string} sans - Sans-serif font family variable name (body text). */
            sans: string;
            /** @property {string} heading - Heading font family variable name. */
            heading: string;
            /** @property {string} mono - Monospace font family variable name. */
            mono: string;
        };
        /** @property {object} logo - Paths to logo images and alt text configuration. */
        logo: {
            /** @property {string} altTextKey - Translation key for the logo's alt text. */
            altTextKey: string;
        };
        /** @property {number} radius - Border radius for elements. */
        radius: number;
    };
    /** @property {object} seo - Contains default settings for Search Engine Optimization. */
    seo: {
        /** @property {string} defaultTitleKey - Translation key for the default page title. */
        defaultTitleKey: string;
        /** @property {string} titleTemplateKey - Translation key for the page title template (e.g., '%s | App Name'). */
        titleTemplateKey: string;
        /** @property {string} defaultDescriptionKey - Translation key for the default meta description. */
        defaultDescriptionKey: string;
        /** @property {string[]} keywords - Default global keywords for SEO. */
        keywords: string[];
        /** @property {string} ogImageType - Default OpenGraph image MIME type. */
        ogImageType: string;
        /** @property {string} ogImageDefaultUrl - Path to the default OpenGraph image (relative to /public). */
        ogImageDefaultUrl: string;
        /** @property {string} ogImageWidth - Default width for OpenGraph images. */
        ogImageWidth: string;
        /** @property {string} ogImageHeight - Default height for OpenGraph images. */
        ogImageHeight: string;
        /** @property {'summary' | 'summary_large_image' | 'app' | 'player'} twitterCard - Default Twitter card type. */
        twitterCard: 'summary' | 'summary_large_image' | 'app' | 'player';
        /** @property {string} twitterSite - Optional Twitter handle associated with the site (e.g., '@YourApp'). */
        twitterSite: string;
    };
    /** @property {object} externalLinks - Contains external links used throughout the application. */
    externalLinks: {
        /** @property {string} twitter - URL to the application's Twitter profile. */
        twitter: string;
    };
    /** @property {object} [featureFlags] - Optional flags to enable/disable application features. */
    featureFlags?: {
        /** @property {boolean} enableAnalytics - Flag to enable analytics. */
        enableAnalytics: boolean;
        /** @property {boolean} enableSubscription - Flag to enable subscription. */
        enableSubscription: boolean;
    };
}

/**
 * @constant {AppConfig} appConfig
 * The main configuration object instance for the application.
 * It provides concrete values based on the AppConfig interface definition.
 * This object is used throughout the frontend to access configuration values
 * like theme settings, language support, SEO defaults, and external links.
 */
export const appConfig: AppConfig = {
    // General App Information
    /** @property {string} appNameKey - Translation key for the application name. Fetched using useTranslation. */
    appNameKey: 'common.appName', // Use translation key
    /** @property {string} appDescriptionKey - Translation key for the default app description. Used in meta tags. */
    appDescriptionKey: 'landing.meta.description', // Default description key
    /** @property {Language[]} supportedLanguages - List of languages supported by the i18n setup. */
    supportedLanguages: ['en', 'es', 'ar', 'ru', 'zh'], // From i18n settings
    /** @property {Language} defaultLanguage - Fallback language used if locale detection fails or is not supported. */
    defaultLanguage: 'en', // From i18n settings

    // Trial settings
    /** @property {number} trialMaxApiCalls - Maximum API calls for a trial user. */
    trialMaxApiCalls: 10, // Adjusted as per shared/plans.ts free tier

    // Theming Configuration
    theme: {
        /** @property {object} colors - References CSS variables defined in `globals.css` for the color scheme. */
        colors: {
            primary: {
                DEFAULT: 'oklch(65% 0.15 85)', // Gold (Light theme)
                foreground: 'oklch(20% 0.03 40)', // Dark Brown (Light theme)
                darker: 'oklch(60% 0.16 85)', // Slightly Darker Gold (Light theme)
            },
            secondary: {
                DEFAULT: 'oklch(45% 0.08 150)', // Muted Green (Light theme)
                foreground: 'oklch(96% 0.01 90)', // Light Cream (Light theme)
            },
            destructive: {
                DEFAULT: 'oklch(55% 0.18 25)', // Muted Red (Light theme)
                foreground: 'oklch(96% 0.01 90)', // Light Cream (Light theme)
            },
            accent: {
                DEFAULT: 'oklch(70% 0.12 95)', // Lighter Gold/Brass (Light theme)
                foreground: 'oklch(20% 0.03 40)', // Dark Brown (Light theme)
            },
            background: 'oklch(var(--background))',
            foreground: 'oklch(var(--foreground))',
            card: {
                DEFAULT: 'oklch(var(--card))',
                foreground: 'oklch(var(--card-foreground))',
            },
            popover: {
                DEFAULT: 'oklch(var(--popover))',
                foreground: 'oklch(var(--popover-foreground))',
            },
            muted: {
                DEFAULT: 'oklch(var(--muted))',
                foreground: 'oklch(var(--muted-foreground))',
            },
            border: 'oklch(var(--border))',
            input: 'oklch(var(--input))',
            ring: 'oklch(var(--primary) / 0.5)', // Ring uses the --primary var for light/dark mode
        },
        /** @property {object} fontFamily - References CSS variables for font families (e.g., from Geist). */
        fontFamily: {
            /** @property {string} sans - Sans-serif font family variable name (body text). */
            sans: 'var(--font-eb-garamond)',
            /** @property {string} heading - Heading font family variable name. */
            heading: 'var(--font-great-vibes)',
            /** @property {string} mono - Monospace font family variable name. */
            mono: 'var(--font-geist-mono)',
        },
        /** @property {object} logo - Configuration for application logos. */
        logo: {
            /** @property {string} altTextKey - Translation key for the logo's alt attribute. */
            altTextKey: 'common.appName', // Use app name key for alt text
        },
        /** @property {number} radius - Border radius for elements. */
        radius: 0.625,
    },

    // Default SEO Meta Values
    /** @property {object} seo - Default SEO settings, often overridden at the page level. */
    seo: {
        /** @property {string} defaultTitleKey - Translation key for the default page title. */
        defaultTitleKey: 'common.appName', // Use app name key as default page title
        /** @property {string} titleTemplateKey - Translation key for the title template (e.g., "%s | App Name"). */
        titleTemplateKey: 'common.titleTemplate', // e.g., "%s | CigarSnap" in translations
        /** @property {string} defaultDescriptionKey - Translation key for the default meta description. */
        defaultDescriptionKey: 'landing.meta.description', // Default meta description key
        /** @property {string[]} keywords - Global fallback keywords for search engines. */
        keywords: [
            'cigar',
            'cigars',
            'cigar scanner',
            'cigar identification',
            'cigar app',
            'humidor',
            'cigar aficionado',
            'cigar community',
            'AI cigar',
        ], // Global fallback keywords
        /** @property {string} ogImageType - Default MIME type for OpenGraph images. */
        ogImageType: 'image/png', // Example type
        /** @property {string} ogImageDefaultUrl - Path to the default OpenGraph image in `/public`. */
        ogImageDefaultUrl: '/cs-og-image.png', // Will replace with actual image later
        /** @property {string} ogImageWidth - Default width for OpenGraph images. */
        ogImageWidth: '1200',
        /** @property {string} ogImageHeight - Default height for OpenGraph images. */
        ogImageHeight: '630',
        /** @property {'summary' | ...} twitterCard - Default Twitter card type. */
        twitterCard: 'summary_large_image',
        /** @property {string} twitterSite - Associated Twitter handle (e.g., '@YourApp'). */
        twitterSite: '@CigarSnapAI', // Example, update if different
    },

    // External Links
    /** @property {object} externalLinks - URLs for external social media profiles or repositories. */
    externalLinks: {
        /** @property {string} twitter - URL for Twitter profile. */
        twitter: 'https://twitter.com/CigarSnapAI', // Example, update if different
    },

    // New feature flags
    featureFlags: {
        enableAnalytics: true,
        enableSubscription: true,
    },
};
